import { useForm } from '@tanstack/react-form';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { z, ZodError } from 'zod';

import styles from '../styles.module.css';
import Button from '@/components/LoginComponents/Button';
import Input from '@/components/LoginComponents/Input';
import { POOL_NAME } from '@/constants';
import { useModal } from '@/context/ModalContext';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { clearStatus, fetchRecover } from '@/slices/authSlice';
import { getUserProfile, initialStateUser } from '@/slices/userProfileSlice';
import { RootState } from '@/store';

const passwordRecoverySchema = z
  .object({
    username: z.string().min(1, 'Email should not be empty'),
    code: z.string().min(1, 'Code should not be empty'),
    password: z.string().min(1, 'Password should not be empty'),
    newPassword: z.string().min(1, 'New Password should not be empty')
  })
  .refine(data => data.password === data.newPassword, {
    message: 'Passwords do not match',
    path: ['newPassword']
  });

function PasswordRecoveryContainer() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const errorResponse = auth?.error?.description;

  const { updateModalContent, closeModal } = useModal();

  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    defaultValues: {
      username: '',
      code: '',
      password: '',
      newPassword: ''
    },
    validators: {
      onSubmit: passwordRecoverySchema
    },
    onSubmit: async ({ value }) => {
      try {
        setIsLoading(true);
        await dispatch(
          fetchRecover({
            code: value.code,
            username: value.username,
            newPassword: value.newPassword,
            reNewPassword: value.newPassword,
            poolName: POOL_NAME
          })
        );
      } finally {
        setIsLoading(false);
      }
    }
  });

  useEffect(() => {
    if (auth?.error?.code === 0 && auth?.status?.code === 200 && !isLoading) {
      updateModalContent(null, {
        modalTitle: 'Password Recovery',
        modalButtons: [
          {
            label: 'OK',
            onClick: () => {
              dispatch(clearStatus());
              dispatch(
                getUserProfile({
                  ...initialStateUser,
                  email: form.getFieldValue('username')
                })
              );
              navigate('/login');
              closeModal();
            },
            variant: 'okBlue'
          }
        ],
        alignButtons: 'center',
        contentTitle: auth?.status?.message
      });
    }
  }, [auth?.error?.code, auth?.status?.message, isLoading]);

  useEffect(() => {
    dispatch(clearStatus());
  }, [dispatch]);

  return (
    <div className={styles.form}>
      {errorResponse && (
        <div className={styles.formErrors}>{errorResponse}</div>
      )}

      <h3>Password Recovery</h3>
      <p className={styles.formDescription}>
        Enter code received via email and set new password
      </p>
      <form.Field name="username">
        {field => (
          <Input
            type="text"
            placeholder="Email"
            value={field.state.value}
            onChange={value => field.handleChange(value || '')}
            errors={field?.state?.meta?.errors as ZodError[]}
          />
        )}
      </form.Field>
      <form.Field name="code">
        {field => (
          <Input
            type="text"
            placeholder="Code"
            value={field.state.value}
            onChange={value => field.handleChange(value || '')}
            errors={field?.state?.meta?.errors as ZodError[]}
          />
        )}
      </form.Field>
      <form.Field name="password">
        {field => (
          <Input
            type="password"
            placeholder="New Password"
            value={field.state.value}
            onChange={value => field.handleChange(value || '')}
            errors={field?.state?.meta?.errors as ZodError[]}
          />
        )}
      </form.Field>
      <form.Field name="newPassword">
        {field => (
          <Input
            type="password"
            placeholder="Confirm Password"
            value={field.state.value}
            onChange={value => field.handleChange(value || '')}
            errors={field?.state?.meta?.errors as ZodError[]}
          />
        )}
      </form.Field>

      <Button
        label="SUBMIT"
        isLoading={isLoading}
        onClick={() => form.handleSubmit()}
      />
    </div>
  );
}

export default PasswordRecoveryContainer;
